import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Card } from '../card';
import { NotificationItem } from './NotificationItem';
import { mapActivityToNotification } from './notifications-utils';
import { useRecentActivityFeed } from '../../features/recent-activity';

export interface NotificationsSectionProps {
    // No props needed - component fetches its own data
}

export const NotificationsSection: React.FC<NotificationsSectionProps> = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    
    // Fetch recent activity data independently
    const { data: activities, isLoading, error } = useRecentActivityFeed();

    const handleViewAll = () => {
        navigate('/recent-activity');
    };

    if (isLoading) {
        return <NotificationsSkeleton />;
    }

    if (error) {
        return null; // Or error state
    }

    const notifications = (activities || []).map(mapActivityToNotification);

    return (
        <section className="w-full">
            {/* Header */}
            <header className="flex items-center justify-between mb-4">
                <h2 className="text-xl text-[#004232] dark:text-white" style={{ fontFamily: 'Cal Sans' }}>
                    {t('notifications.title')}
                </h2>

                <button
                    onClick={handleViewAll}
                    className="font-semibold text-sm text-[#004232] dark:text-white hover:opacity-80 transition-opacity"
                    aria-label={t('notifications.viewAll')}
                >
                    {t('notifications.viewAll')}
                </button>
            </header>

            {/* Notifications List */}
            <Card className="bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-2xl shadow-sm">
                <div className="max-h-[600px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600">
                    {notifications.length === 0 ? (
                        <div className="flex flex-col items-center justify-center min-h-[200px] text-gray-500 dark:text-gray-400 px-5 py-[15px]">
                            <p className="text-sm">{t('notifications.empty')}</p>
                        </div>
                    ) : (
                        <ul className="flex flex-col px-5 py-2">
                            {notifications.map((notification, index) => (
                                <NotificationItem
                                    key={notification.id}
                                    notification={notification}
                                    isLast={index === notifications.length - 1}
                                />
                            ))}
                        </ul>
                    )}
                </div>
            </Card>
        </section>
    );
};

// Loading skeleton component
const NotificationsSkeleton: React.FC = () => {
    return (
        <section className="w-full">
            <div className="h-8 w-32 bg-gray-200 dark:bg-gray-700 rounded mb-4 animate-pulse" />
            <Card className="bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-2xl">
                <div className="px-5 py-2">
                    {[...Array(5)].map((_, i) => (
                        <div key={i} className="flex items-start gap-4 py-[15px] border-b border-slate-200 dark:border-gray-700">
                            <div className="w-11 h-11 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse" />
                            <div className="flex-1 space-y-2">
                                <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-3/4" />
                                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-1/4" />
                            </div>
                        </div>
                    ))}
                </div>
            </Card>
        </section>
    );
};

